// OccurrenceStrip — compact 8x8 tile heatmap summarising every child
// occurrence of a recurring deliverable, rendered on the parent row
// (brand skill §9.7). Tone precedence: explicit RAG wins; otherwise
// the locked status palette (accepted=emerald, submitted/under-review
// =sky, revision-requested/late/conditionally-accepted=amber,
// awaiting-resubmission=rose, overdue=red, rejected/closed/
// not-started=muted slate). The caller supplies `rag` as the
// EFFECTIVE rag (red for overdue children is derived upstream via
// deriveEffectiveRag).
//
// Caps at 16 tiles showing the most-recent occurrences with a "+N more"
// affordance for the older ones; aria-label summarises all occurrences.
//
// Action-set anchor: the FIRST occurrence whose derived status is in
// the action set (overdue / late / awaiting-resubmission /
// revision-requested / rejected) renders as a 16px rounded-md tile
// with a soft halo ring at /[0.18] opacity. The anchor is always
// pulled into the visible window even if it would otherwise sit past
// the 16-tile cap. failed-acceptance from the proposal action-set is
// excluded here because it's an event type, not a derived status —
// the resting state when the supplier hasn't yet resubmitted is
// awaiting-resubmission.

import type { DeliverableDerivedStatus, RagRating } from "../../../../types";

export interface OccurrenceTile {
  id: string;
  status: DeliverableDerivedStatus;
  rag: RagRating | null;
  period: string;
}

export interface OccurrenceStripProps {
  occurrences: OccurrenceTile[];
  /** v3 surfacing: when the strip is rendered on a parent row, the
   *  caller passes the id of the tile that represents the schedule's
   *  surfaced occurrence so it can carry a ring. Missing or
   *  unmatched id → no ring on any tile. */
  surfacedId?: string;
  /** When the surfaced occurrence is overdue, the ring is red. When
   *  false (default) it is brand-violet. Ignored unless `surfacedId`
   *  matches a visible tile. */
  urgent?: boolean;
}

const MAX_VISIBLE = 16;

const RAG_TONE: Record<RagRating, string> = {
  green: "bg-emerald-600 dark:bg-emerald-400",
  amber: "bg-amber-600 dark:bg-amber-400",
  red: "bg-red-600 dark:bg-red-400",
};

const MUTED_TONE = "bg-slate-400/40 dark:bg-slate-500/40";
// Status-driven palette (used when no explicit RAG is set on the tile).
// Mirrors the locked status palette — `late` borrows the same amber
// tone as the Late status pill (it is awaiting customer review of a
// delayed delivery, not a hard breach) and `overdue` is the only state
// that goes red. Accepted goes emerald so completed cycles read as
// success at a glance.
const STATUS_TONE: Record<DeliverableDerivedStatus, string> = {
  accepted: "bg-emerald-600 dark:bg-emerald-400",
  rejected: MUTED_TONE,
  closed: MUTED_TONE,
  submitted: "bg-sky-500 dark:bg-sky-400",
  "under-review": "bg-sky-500 dark:bg-sky-400",
  "revision-requested": "bg-amber-600 dark:bg-amber-400",
  "conditionally-accepted": "bg-amber-600 dark:bg-amber-400",
  "awaiting-resubmission": "bg-rose-600 dark:bg-rose-400",
  late: "bg-amber-600 dark:bg-amber-400",
  overdue: "bg-red-600 dark:bg-red-400",
  "not-started": MUTED_TONE,
};

function tileClass(t: OccurrenceTile): string {
  if (t.rag) return RAG_TONE[t.rag];
  return STATUS_TONE[t.status];
}

// Action-set anchor: derived statuses that mark a tile as needing
// action. failed-acceptance (proposal action-set) is excluded — it's
// an event type, not a derived status; awaiting-resubmission IS the
// derived resting state for that lane.
const ANCHOR_ACTION_SET: ReadonlySet<DeliverableDerivedStatus> = new Set([
  "overdue",
  "late",
  "awaiting-resubmission",
  "revision-requested",
  "rejected",
]);

// Halo ring colour per anchor tone. Uses Tailwind arbitrary-opacity
// `/[0.18]` to match the brand-doc-specified halo intensity (the
// nearest published opacity step is /15 or /20; /[0.18] hits the spec
// exactly).
const ANCHOR_RING_CLASS: Record<DeliverableDerivedStatus, string> = {
  accepted: "ring-2 ring-emerald-300/[0.18]",
  rejected: "ring-2 ring-red-300/[0.18]",
  closed: "ring-2 ring-slate-300/[0.18]",
  submitted: "ring-2 ring-sky-300/[0.18]",
  "under-review": "ring-2 ring-sky-300/[0.18]",
  "revision-requested": "ring-2 ring-amber-300/[0.18]",
  "conditionally-accepted": "ring-2 ring-amber-300/[0.18]",
  "awaiting-resubmission": "ring-2 ring-rose-300/[0.18]",
  late: "ring-2 ring-amber-300/[0.18]",
  overdue: "ring-2 ring-red-300/[0.18]",
  "not-started": "ring-2 ring-slate-300/[0.18]",
};

const STATUS_LABEL: Record<DeliverableDerivedStatus, string> = {
  "not-started": "not started",
  submitted: "submitted",
  "under-review": "under review",
  "revision-requested": "revision requested",
  "conditionally-accepted": "conditionally accepted",
  "awaiting-resubmission": "awaiting resubmission",
  accepted: "accepted",
  rejected: "rejected",
  closed: "closed",
  overdue: "overdue",
  late: "late",
};

function composeAriaLabel(occurrences: OccurrenceTile[]): string {
  let red = 0;
  let amber = 0;
  let green = 0;
  let overdue = 0;
  let submitted = 0;
  let notYetDue = 0;
  for (const o of occurrences) {
    if (o.rag === "red") red += 1;
    else if (o.rag === "amber") amber += 1;
    else if (o.rag === "green") green += 1;
    else if (o.status === "overdue") overdue += 1;
    else if (o.status === "submitted") submitted += 1;
    else notYetDue += 1;
  }
  const parts: string[] = [`${occurrences.length} occurrences`];
  if (red) parts.push(`${red} red`);
  if (amber) parts.push(`${amber} amber`);
  if (green) parts.push(`${green} green`);
  if (overdue) parts.push(`${overdue} overdue`);
  if (submitted) parts.push(`${submitted} submitted`);
  if (notYetDue) parts.push(`${notYetDue} not yet due`);
  return parts.join(", ");
}

export default function OccurrenceStrip({
  occurrences,
  surfacedId,
  urgent = false,
}: OccurrenceStripProps) {
  if (occurrences.length === 0) return null;

  // Find the FIRST action-set occurrence. The anchor is rendered
  // enlarged with a halo ring; if it would otherwise sit past the
  // default last-MAX_VISIBLE slice, it gets pulled into the visible
  // window by displacing the oldest non-action tile.
  const anchorIndex = occurrences.findIndex((o) =>
    ANCHOR_ACTION_SET.has(o.status),
  );
  const anchor = anchorIndex !== -1 ? occurrences[anchorIndex] : null;

  // Default visible slice — most-recent MAX_VISIBLE.
  let visible =
    occurrences.length > MAX_VISIBLE
      ? occurrences.slice(-MAX_VISIBLE)
      : occurrences;

  // Pull the anchor into view if it sits outside the default slice.
  // Replace the OLDEST visible tile so the anchor's relative position
  // at the head of the strip signals "earliest unresolved" to the
  // reader; trade-off documented at the top of the file.
  if (anchor && !visible.includes(anchor)) {
    visible = [anchor, ...visible.slice(1)];
  }

  const overflow = occurrences.length - visible.length;

  // The surfaced ring uses Tailwind's `ring-2` + a coloured `ring-`
  // utility. Brand violet is the default; red kicks in only when the
  // schedule's surfaced occurrence is overdue.
  const surfacedRingClass = urgent
    ? "ring-2 ring-red-400"
    : "ring-2 ring-brand-400";

  return (
    <div
      data-testid="occurrence-strip"
      role="img"
      aria-label={composeAriaLabel(occurrences)}
      className="inline-flex items-center gap-0.5"
    >
      {visible.map((t) => {
        const isSurfaced = surfacedId !== undefined && t.id === surfacedId;
        const isAnchor = anchor !== null && t.id === anchor.id;
        // Surfaced tile carries a semantic aria-label that names the
        // period AND tells the screen-reader user why it stands out
        // ("…, surfaced as next due" / "…, surfaced as overdue").
        // Non-surfaced tiles fall back to the `title` attribute which
        // already names the period + status.
        const ariaLabel = isSurfaced
          ? `${t.period}, surfaced as ${urgent ? "overdue" : "next due"}`
          : undefined;
        const sizeClass = isAnchor
          ? "inline-block w-3.5 h-3.5 rounded-md"
          : "inline-block w-2 h-2 rounded-sm";
        const rings = [
          isAnchor ? ANCHOR_RING_CLASS[t.status] : null,
          isSurfaced ? surfacedRingClass : null,
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <span
            key={t.id}
            data-testid={`occurrence-tile-${t.id}`}
            data-anchor={isAnchor ? "true" : undefined}
            title={`${t.period} · ${STATUS_LABEL[t.status]}`}
            aria-label={ariaLabel}
            className={`${sizeClass} ${tileClass(t)}${
              rings ? ` ${rings}` : ""
            }`}
          />
        );
      })}
      {overflow > 0 && (
        <span
          data-testid="occurrence-strip-more"
          className="ml-1 text-caption text-slate-500 dark:text-slate-400"
        >
          +{overflow} more
        </span>
      )}
    </div>
  );
}
